/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { HTMLProps, ReactElement, ReactNode } from "react"
import MedaicaLogo from "@medaica/common/components/medaica-logo"
import { Size, useElementSize } from "@medaica/common/views/exam/virtual-exam/video-layout"

/**
 * The live exam page: the logo, the main area and the footer, stacked from the top of the page. `main` is given the
 * space the logo and footer leave on the screen, so that the exam's video can be as large as fits without pushing the
 * footer off the screen. When the video is smaller, the footer follows straight after it.
 */
const Layout = ({
  main,
  overlays,
  footer,
  ...props
}: {
  main: (area: Size) => ReactNode
  overlays?: ReactNode
  footer?: ReactNode
} & HTMLProps<HTMLDivElement>): ReactElement => {
  const [pageRef, page] = useElementSize()
  const [headerRef, header] = useElementSize("border-box")
  const [footerRef, footerSize] = useElementSize("border-box")

  return (
    <div ref={pageRef} className="h-screen flex flex-col px-5 bg-gray-100" {...props}>
      <div ref={headerRef} className="py-5 flex flex-shrink-0">
        <MedaicaLogo className="text-primary-700 h-7" />
      </div>
      {overlays}
      <div className="flex flex-shrink-0">
        {main({ width: page.width, height: page.height - header.height - footerSize.height })}
      </div>
      {footer && (
        <div ref={footerRef} className="flex-shrink-0 px-2 pt-4 pb-5">
          {footer}
        </div>
      )}
    </div>
  )
}

export default Layout
